import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_PP,
  RECEIVE_ALL_PP,
  RECEIVE_PP_METADATA,
  INCREMENT_PP_COUNT,
  RECEIVE_PP_LINES,
  RECEIVE_ALL_PP_LINES,
  RECEIVING_PP_LINES,
  RECEIVE_PP_LINES_METADATA,
  RECEIVE_PP_LINES_FAILURE,
  SAVING_PP_LINES,
  UPDATING_PP_LINES,
  CLEAR_PP_STATE,
  CLEAR_STATE,
  RESET_PP_META,
  UPDATE_CURRENT_PP_PAGE,
  DELETE_PP_LINES,
  CLEAR_PP_TRX_LINES
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import { fetchLines, getLineIds } from '@/store/modules/helpers/actionHelpers'
import {
  receiveData,
  increment,
  receiveMetadata,
  resetMetadata,
  receiveLinesMetadata,
  clearState,
  deleteLine,
  clearTrxLines
} from '@/store/modules/helpers/mutationHelpers'

const lineSchema = new schema.Entity('lines')
const ppSchema = new schema.Entity('pps', { lines: new schema.Array(lineSchema) })
const ppListSchema = new schema.Array(ppSchema)

const state = {
  cache: {},
  all: [],
  allLines: [],
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  lines: {},
  lineCount: null,
  nextLineUrl: null,
  prevLineUrl: null,
  receivingLines: null,
  savingLines: null,
  updatingLines: null
}

const actions = {
  /**
   * Fetches one or more PPs with the option to pass query string filter parameters
   * @param store
   * @param opts
   * @return {Promise<*>}
   */
  async fetchPpsWithQuery (store, opts) {
    let query
    let storeLines = false
    if (opts) {
      query = opts.hasOwnProperty('query') ? opts.query : opts
      storeLines = opts.hasOwnProperty('storeLines') ? opts.storeLines : false
    } else {
      query = null
    }
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.get(buildUrlWithQuery('pps', query, instanceUrl), { headers })
        .then(checkStatus)
        .then(parseJSON)
      this.commit(RESET_PP_META)
      let { results, next, previous, count } = resp
      const { result, entities } = normalize(results, ppListSchema)
      if (!(result && Array.isArray(result))) {
        throw new Error('No result set in response')
      }
      this.commit(RECEIVE_PP, { data: entities.pps })
      this.commit(RECEIVE_PP_METADATA, { next, previous, count, result: result.length })
      this.commit(UPDATE_CURRENT_PP_PAGE, 1)
      if (storeLines) {
        const lines = await fetchLines.bind(this)({ lineIds: getLineIds(entities.pps), type: 'pp' })
        this.commit(RECEIVE_PP_LINES, { id: null, data: lines })
      }
      return { query, count, entities }
    } catch (e) {
      console.log(e)
    }
  },
  /**
   * Fetches all available PPs
   * @param store
   * @return {Promise<*>}
   */
  async fetchAllPps (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.get(buildUrl('pps?serialize=false', null, instanceUrl), { headers })
        .then(checkStatus)
      this.commit(RECEIVE_ALL_PP, { data: resp.body })
      return resp.body
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Fetches all available PP lines
   * @param store
   * @return {Promise<*>}
   */
  async fetchAllPpLines (store) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.get(buildUrl('lines/pp?serialize=false', null, instanceUrl), { headers })
        .then(checkStatus)
      this.commit(RECEIVE_ALL_PP_LINES, { data: resp.body })
      return resp.body
    } catch (e) {
      console.log(e)
      this.commit(RECEIVE_PP_LINES_FAILURE, e.statusText)
    }
  },
  /**
   * Saves a PP transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async savePp (store, payload) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.post(buildUrl('pps', null, instanceUrl), payload, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to save PP`)
      }
      const { entities, result } = normalize(resp, ppSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when saving PP`)
      }
      const lines = await fetchLines.bind(this)({ lineIds: getLineIds(entities.pps), type: 'pp' })
      this.commit(RECEIVE_PP, { data: entities.pps })
      this.commit(RECEIVE_PP_LINES, { id: null, data: lines })
      this.commit(INCREMENT_PP_COUNT, 1)
      return resp
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Updates a PP transaction
   * @param store
   * @param payload
   * @return {Promise<*>}
   */
  async updatePp (store, payload) {
    const { id, data } = payload
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      const resp = await Vue.http.patch(buildUrl(`pps/${id}`, null, instanceUrl), data, { headers })
        .then(checkStatus)
        .then(parseJSON)
      if (!resp) {
        throw new Error(`Unable to update PP with id ${id}`)
      }
      const { entities, result } = normalize(resp, ppSchema)
      if (!result || (Array.isArray(result) && !result.length)) {
        throw new Error(`Error normalizing response when updating PP with id ${id}`)
      }
      const lines = await fetchLines.bind(this)({ lineIds: getLineIds(entities.pps), type: 'pp' })
      this.commit(RECEIVE_PP, { data: entities.pps })
      this.commit(RECEIVE_PP_LINES, { id: null, data: lines })
      return resp
    } catch (e) {
      console.log(e)
      return e
    }
  },
  /**
   * Deletes a PP transaction
   * @param store
   * @param id
   * @return {Promise<*>}
   */
  async deletePp (store, id) {
    const instanceUrl = this.getters.getActiveInstance.url
    const headers = getHeaders(this.getters.getToken)
    try {
      return await Vue.http.delete(buildUrlWithQuery(`pps/${id}`, null, instanceUrl), { headers })
        .then(checkStatus)
    } catch (e) {
      console.log(e)
      return e
    }
  },
  clearPpState (store, payload = null) {
    return this.commit(CLEAR_PP_STATE, payload)
  },
  clearPpTransactionLines (store, trxId) {
    this.commit(CLEAR_PP_TRX_LINES, trxId)
  },
  resetPpMeta () {
    this.commit(RESET_PP_META)
  }
}

const getters = {
  getPps: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getPpById: (state) => (id) => {
    if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getAllPps: (state) => {
    return state.all || []
  },
  getPpMetadata: (state) => {
    const { shown, count, next, previous } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown, next, previous }
  },
  getPpLines: (state) => {
    return state.lines || {}
  },
  getAllPpLines: (state) => {
    return state.allLines || []
  },
  getPpLineById: (state) => (id) => {
    const { lines } = state
    for (let l in lines) {
      if (!lines.hasOwnProperty(l)) continue
      if (parseInt(lines[l].id) === parseInt(id)) return lines[l]
    }
    return {}
  },
  getPpLinesByTransactionId: (state) => (id) => {
    const { lines } = state
    if (!Object.keys(state.lines).length) return {}
    let ret = {}
    for (let l in state.lines) {
      if (lines[l].hasOwnProperty('transaction_id') && parseInt(lines[l].transaction_id) === parseInt(id)) {
        ret[l] = lines[l]
      }
    }
    return ret
  },
  getNextPpLineSet: (state) => {
    return state.nextLineUrl
  },
  getPrevPpLineSet: (state) => {
    return state.prevLineUrl
  },
  getPpsByNumber: (state) => (number) => {
    return Object.values(state.cache).filter(v => v.number.includes(number))
  },
  getCurrentPpPage: (state) => {
    if (state.currentPage) return state.currentPage
  }
}

const mutations = {
  [RECEIVE_PP] (state, results) {
    for (let d in results.data) {
      results.data[d]['transactionType'] = 'Deposit'
    }
    receiveData(state, results)
  },
  [RECEIVE_ALL_PP] (state, results) {
    const { data } = results
    state.all = data
    return true
  },
  [RECEIVE_PP_LINES] (state, payload) {
    const { id, data } = payload
    if (!(data && (Array.isArray(data) || Object.keys(data).length))) {
      return state.lines
    }
    let tempLineState = { ...state.lines }
    let newData = {}
    if (Array.isArray(data) && data.every(line => !!line.id)) {
      data.forEach(line => (newData[line.id] = line))
    } else {
      newData = data
    }
    if (tempLineState && Object.keys(tempLineState).length) {
      tempLineState = { ...tempLineState, ...newData }
    } else {
      tempLineState = { ...newData }
    }
    if (id) {
      let tempState = { ...state.data }
      for (let d in newData) {
        if (tempState[id] && tempState[id].hasOwnProperty('lines') && Array.isArray(tempState[id].lines)) {
          tempState[id].lines = [...new Set([...tempState[id].lines, newData[d].id])]
        }
      }
      state.data = { ...tempState }
    }
    state.lines = { ...tempLineState }
  },
  [RECEIVING_PP_LINES] (state, payload) {
    state.receivingLines = { status: payload, err: null }
  },
  [RECEIVE_ALL_PP_LINES] (state, results) {
    const { data } = results
    state.allLines = data
    return true
  },
  [SAVING_PP_LINES] (state, payload) {
    state.savingLines = { status: payload.status, err: null }
  },
  [UPDATING_PP_LINES] (state, payload) {
    state.updatingLines = { status: payload, err: null }
  },
  [INCREMENT_PP_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_PP_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [RECEIVE_PP_LINES_METADATA] (state, payload) {
    receiveLinesMetadata(state, payload)
  },
  [UPDATE_CURRENT_PP_PAGE] (state, payload) {
    state.currentPage = payload
  },
  [CLEAR_PP_STATE] (state, payload) {
    let newState = {
      data: Object.assign({}),
      lines: Object.assign({}),
      deleting: false
    }
    if (payload) {
      if (Array.isArray(payload) && payload.length) {
        for (let i = 0; i < payload.length; i++) {
          if (newState.hasOwnProperty(payload[i])) delete newState[payload[i]]
        }
      } else {
        newState['cache'] = Object.assign({})
      }
    }
    for (let key in newState) {
      state[key] = newState[key]
    }
  },
  [CLEAR_PP_TRX_LINES] (state, payload) {
    clearTrxLines(state, payload)
  },
  [RESET_PP_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  },
  [DELETE_PP_LINES] (state, payload) {
    deleteLine(state, payload)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/pp.js